import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Activity, Loader2, RefreshCw } from "lucide-react";
import { useSkills, useCurrentOrgId, type SkillRow } from "@/hooks/useSkillOS";

function qualityColor(score: number) {
  if (score >= 75) return "text-emerald-500";
  if (score >= 50) return "text-amber-500";
  return "text-destructive";
}

function qualityBadge(score: number) {
  if (score >= 75) return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Excelente</Badge>;
  if (score >= 50) return <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">Regular</Badge>;
  return <Badge variant="destructive">Fraco</Badge>;
}

export default function SkillAnalyzerPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: skills = [] } = useSkills();
  const { data: orgId } = useCurrentOrgId();

  const activeSkills: SkillRow[] = skills.filter((s) => s.active);

  const analyzeMut = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("skill-analyzer", { body: {} });
      if (error) throw error;
      return data as { analyzed?: number };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["skill-os", "skills"] });
      toast({ title: "Análise concluída", description: `${data?.analyzed ?? 0} skills analisadas` });
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const syncMut = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Sem organização");
      const { data, error } = await supabase.functions.invoke("knowledge-sync", { body: { org_id: orgId } });
      if (error) throw error;
      return data as { synced?: number };
    },
    onSuccess: (data) => toast({ title: "Sync concluído", description: `${data?.synced ?? 0} artigos sincronizados` }),
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const sortedByQuality = [...activeSkills].sort((a, b) => (a.quality_score ?? 0) - (b.quality_score ?? 0));
  const avgQuality = activeSkills.length > 0
    ? Math.round(activeSkills.reduce((a, s) => a + (s.quality_score ?? 0), 0) / activeSkills.length)
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => analyzeMut.mutate()} disabled={analyzeMut.isPending}>
          {analyzeMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Activity className="w-4 h-4 mr-1" />}
          Analisar Qualidade
        </Button>
        <Button variant="outline" size="sm" onClick={() => syncMut.mutate()} disabled={syncMut.isPending || !orgId}>
          {syncMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Sync Knowledge Base
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Qualidade Média</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-4xl font-bold font-headline ${qualityColor(avgQuality)}`}>{avgQuality}/100</div>
            <Progress value={avgQuality} className="mt-2 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Skills por Qualidade</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {sortedByQuality.length === 0 ? (
              <p className="text-sm text-muted-foreground">Execute a análise para ver resultados.</p>
            ) : (
              sortedByQuality.map((s) => {
                const q = s.quality_score ?? 0;
                return (
                  <div key={s.id} className="flex items-center justify-between">
                    <span className="text-sm">{s.name}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${qualityColor(q)}`}>{q}</span>
                      {qualityBadge(q)}
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
